import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { AppPagination, BD } from "../types";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";

type BDListResponse = AppPagination<BD> | BD[];

const fetchBDs = async ({
  page,
  limit,
  isActive,
  search,
  fromDate,
  toDate,
  sortBy,
}: {
  page: number;
  limit: number;
  isActive: string;
  search: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  sortBy: string;
}) => {
  const query = new URLSearchParams();
  query.append("page", page.toString());
  query.append("limit", limit.toString());
  if (isActive) query.append("isActive", isActive);
  if (search.trim()) query.append("search", search.trim());
  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));
  if (sortBy) query.append("sortBy", sortBy);

  const response = await axios.get<BDListResponse>(
    `${ServerRoutes.bd}?${query.toString()}`
  );
  return response.data;
};

const defaultSort = "createdAt";

export function BDSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isActive, setIsActive] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [sortBy, setSortBy] = useState(defaultSort);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [identifierType, setIdentifierType] = useState("userId");
  const [identifierValue, setIdentifierValue] = useState("");
  const [referralLimit, setReferralLimit] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, isLoading, isError, error, refetch } = useQuery<BDListResponse, Error>({
    queryKey: ["bd", page, limit, isActive, search, fromDate?.format(), toDate?.format(), sortBy],
    queryFn: () =>
      fetchBDs({ page, limit, isActive, search, fromDate, toDate, sortBy }),
  });

  const items = useMemo(() => {
    if (Array.isArray(data)) return data;
    return data?.items ?? [];
  }, [data]);

  const totalPages = useMemo(() => {
    if (Array.isArray(data)) return 1;
    return data?.meta?.totalPages ?? 1;
  }, [data]);

  const handleOpenCreate = () => {
    setIdentifierType("userId");
    setIdentifierValue("");
    setReferralLimit("");
    setNotes("");
    setIsCreateOpen(true);
  };

  const handleCloseCreate = () => {
    if (!isSubmitting) setIsCreateOpen(false);
  };

  const identifierLabel = useMemo(() => {
    if (identifierType === "telegramId") return "Telegram ID";
    if (identifierType === "inviteCode") return "Invite Code";
    return "User ID";
  }, [identifierType]);

  const isIdentifierValid = useMemo(() => {
    if (!identifierValue.trim()) return false;
    if (identifierType === "userId") {
      return !Number.isNaN(Number(identifierValue));
    }
    return true;
  }, [identifierType, identifierValue]);

  const handleCreateBD = async () => {
    if (!isIdentifierValid) return;
    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {};
      if (identifierType === "userId") {
        payload.userId = Number(identifierValue);
      } else if (identifierType === "telegramId") {
        payload.telegramId = identifierValue.trim();
      } else {
        payload.inviteCode = identifierValue.trim();
      }

      if (referralLimit.trim()) {
        payload.referralLimit = Number(referralLimit);
      }
      if (notes.trim()) {
        payload.notes = notes.trim();
      }

      await axios.post(ServerRoutes.bd, payload);
      setSnackbarMessage("BD user created successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsCreateOpen(false);
      refetch();
    } catch (createError) {
      setSnackbarMessage("Failed to add BD user.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleResetFilters = () => {
    setIsActive("");
    setSearch("");
    setFromDate(null);
    setToDate(null);
    setSortBy(defaultSort);
    setPage(1);
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          BD Program
        </Typography>
        <Button variant="contained" onClick={handleOpenCreate}>
          Add BD
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Username, telegram ID, email"
            sx={{ minWidth: isMobile ? "100%" : 240 }}
          />
          <FormControl size="small" sx={{ minWidth: isMobile ? "100%" : 160 }}>
            <InputLabel id="bd-active-label">Status</InputLabel>
            <Select
              labelId="bd-active-label"
              label="Status"
              value={isActive}
              onChange={(event) => {
                setIsActive(event.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? "100%" : 180 }}>
            <InputLabel id="bd-sort-label">Sort By</InputLabel>
            <Select
              labelId="bd-sort-label"
              label="Sort By"
              value={sortBy}
              onChange={(event) => {
                setSortBy(event.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="createdAt">Created At</MenuItem>
              <MenuItem value="referralCount">Referral Count</MenuItem>
              <MenuItem value="username">Username</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={(date) => {
              setFromDate(date);
              setPage(1);
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={(date) => {
              setToDate(date);
              setPage(1);
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button variant="text" onClick={handleResetFilters}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
        {isError ? (
          <Box p={3} textAlign="center">
            <Typography color="error" variant="body1">
              {(error as Error).message}
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box p={2}>
            {[...Array(4)].map((_, index) => (
              <Box key={index} display="flex" gap={2} py={2} px={2}>
                <Skeleton variant="text" width="15%" height={30} />
                <Skeleton variant="text" width="20%" height={30} />
                <Skeleton variant="text" width="20%" height={30} />
                <Skeleton variant="text" width="20%" height={30} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Invite Code</TableCell>
                  <TableCell>Referral Limit</TableCell>
                  <TableCell>Current Referrals</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No BD users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((bd) => (
                    <TableRow key={bd.id}>
                      <TableCell>{bd.id}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {bd.user?.username || bd.user?.firstName || "—"}
                          </Typography>
                          {bd.user?.id ? (
                            <Typography variant="caption" color="text.secondary">
                              ID: {bd.user.id}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{bd.user?.telegramId || "—"}</TableCell>
                      <TableCell>{bd.user?.email || "—"}</TableCell>
                      <TableCell>{bd.user?.inviteCode || "—"}</TableCell>
                      <TableCell>{bd.referralLimit}</TableCell>
                      <TableCell>{bd.currentReferrals}</TableCell>
                      <TableCell>
                        <Chip
                          label={bd.isActive ? "Active" : "Inactive"}
                          color={bd.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{bd.notes || "—"}</TableCell>
                      <TableCell>
                        {bd.createdAt
                          ? new Date(bd.createdAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View BD">
                          <IconButton
                            onClick={() => navigate(`/bd/${bd.id}`)}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size={isMobile ? "small" : "large"}
        />
      </Box>

      <Dialog open={isCreateOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add BD User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="bd-identifier-label">Add By</InputLabel>
              <Select
                labelId="bd-identifier-label"
                label="Add By"
                value={identifierType}
                onChange={(event) => {
                  setIdentifierType(event.target.value);
                  setIdentifierValue("");
                }}
              >
                <MenuItem value="userId">User ID</MenuItem>
                <MenuItem value="telegramId">Telegram ID</MenuItem>
                <MenuItem value="inviteCode">Invite Code</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={identifierLabel}
              value={identifierValue}
              onChange={(event) => setIdentifierValue(event.target.value)}
              fullWidth
              type={identifierType === "userId" ? "number" : "text"}
              error={Boolean(identifierValue.trim()) && !isIdentifierValid}
              helperText={
                !identifierValue.trim()
                  ? ""
                  : !isIdentifierValid
                  ? "Enter a valid value."
                  : ""
              }
            />
            <TextField
              label="Referral Limit"
              value={referralLimit}
              onChange={(event) => setReferralLimit(event.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateBD}
            disabled={!isIdentifierValid || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}
